import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Code, Zap, Shield } from 'lucide-react';

const sampleCode = `/**
 * A test suite demonstrating NodeBoot app testing using node:test
 * with Mongo persistence, DI, and runtime hooks.
 */
describe("Sample Node-Boot Persistence Test", () => {
    // Initialize NodeBoot with configuration and Mongo container
    const {useRepository, useService} = useNodeBoot(
        NodeBootApp,
        ({useConfig, usePactum, useMongoContainer}) => {
            useConfig({
                app: {port: 20000},
            });

            useMongoContainer({
                dbName: "test-db",
                image: "mongo:8",
            });

            usePactum();
        },
    );

    it("should retrieve data from repository", async () => {
        const userRepository = useRepository(UserRepository);
        // This should no longer hang, since we’re running in the same async context
        const users = await userRepository.find({});
        assert.ok(users, "Expected repository to return users");
    });

    it("should retrieve data from the service", async () => {
        const userService = useService(UserService);
        const users = await userService.findAllUser();
        assert.ok(users, "Expected service to return users");
    });

    it("should persist and retrieve data", async () => {
        const userService = useService(UserService);

        await userService.createUser({
            email: "ney.br.santos@gmail.com",
            name: "Manuel Santos",
            password: "123456789",
        });

        // This should no longer hang, since we’re running in the same async context
        const users = await userService.findAllUser();

        assert.strictEqual(users.length, 1, "Expected 1 user stored");
    });

    it("should retrieve data via HTTP", async () => {
        await spec()
            .post('/api/users/')
            .withJson({
                name: 'bolt',
                email: 'bolt@swift.run',
                password: "123456789"
            })
            .expectStatus(200);

       const response = await spec()
            .get('/api/users/')
            .returns("res.body");

         assert.strictEqual(response.length, 2, "Expected 2 users stored");
    });
});`;

const features = [
  {
    icon: Code,
    title: 'Clean & Intuitive',
    description: 'Write tests that read like documentation with our hook-based API',
  },
  {
    icon: Zap,
    title: 'Fast Setup',
    description: 'Get started in minutes with zero configuration and smart defaults',
  },
  {
    icon: Shield,
    title: 'Type-Safe',
    description: 'Full TypeScript support with comprehensive type definitions',
  },
];

export const FrameworkInfo = () => {
  return (
    <section className="framework-info-section">
      <div className="container">
        <div className="info-header">
          <h2 className="info-title">Why NodeBoot Test Framework?</h2>
          <p className="info-subtitle">
            A modern, plugin-based testing framework designed for NodeBoot applications.
            Write less boilerplate, focus on what matters.
          </p>
        </div>

        <div className="info-features">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div key={index} className="info-feature-card">
                <div className="info-feature-icon">
                  <IconComponent size={28} />
                </div>
                <h3 className="info-feature-title">{feature.title}</h3>
                <p className="info-feature-description">{feature.description}</p>
              </div>
            );
          })}
        </div>

        <div className="code-showcase">
          <div className="code-showcase-header">
            <h3 className="code-showcase-title">See It In Action</h3>
            <p className="code-showcase-description">
              Here's a complete test example with database integration, service testing, and HTTP assertions.
            </p>
          </div>
          <div className="code-showcase-content">
            <SyntaxHighlighter
              language="typescript"
              style={vscDarkPlus}
              customStyle={{
                borderRadius: '16px',
                padding: '32px',
                fontSize: '14px',
                margin: 0,
                backgroundColor: '#1a1a1a',
                border: '2px solid rgba(5, 181, 89, 0.2)',
              }}
              showLineNumbers
            >
              {sampleCode}
            </SyntaxHighlighter>
          </div>
        </div>
      </div>
    </section>
  );
};
